<script setup lang="ts">
import {ref, computed} from "vue";
import {useI18n} from "vue-i18n";

import {Modal} from "@/components/Global";
import FCheck from "@/lib/components/ui/FCheck.vue";
import FChip from "@/lib/components/ui/FChip.vue";
import FTextField from "@/lib/components/ui/FTextField.vue";

import {SchedulingOperation} from "@/interfaces";
import {OF_STATUS} from "@/config/constants";

import {useValidateForms} from "@/composables/useValidateForms";
import {sanitizeNumeralInput} from "@/lib/utils/sanitizer";

const {t} = useI18n();

const props = defineProps<{
  op: SchedulingOperation;
  nextOp?: SchedulingOperation;
  isLoading?: boolean;
  toggleModal: () => void;
}>();

const emit = defineEmits<{
  (
    e: "validate-op",
    params: {
      addedRejectQuantity: number;
      addedProducedQuantity: number;
      shouldCloseOP: boolean;
    },
  ): void;
}>();

// We want the calculated values to be correct when the OP has not been started
// (in which case it most likely won't have any available quantity)
const availableQuantity = computed(() => {
  const {quantite_of, quantite_of_disponible, op_status} = props.op;

  return (
    (op_status === OF_STATUS.PENDING && !quantite_of_disponible
      ? quantite_of
      : quantite_of_disponible) || 0
  );
});

const rejectQuantity = ref<number>(0);
const producedQuantity = ref<number>(availableQuantity.value);
const shouldCloseOperation = ref<boolean>(false);

const {errors: validationErrors, validateForms} = useValidateForms(
  [{producedQuantity, rejectQuantity}],
  [
    {
      producedQuantity: {
        type: "number",
        min: 0,
      },
      rejectQuantity: {
        type: "number",
        min: 0,
      },
    },
  ],
);

const dynamicSummaries = computed(() => [
  {
    icon: "box",
    key: "quantity_remaining",
    value: Math.max(
      0,
      availableQuantity.value - producedQuantity.value - rejectQuantity.value,
    ),
  },
  {
    icon: "box",
    key: "quantity_total",
    value: Math.max(0, (props.op.quantite_of || 0) - rejectQuantity.value),
  },
]);

const hasMadeAnyChange = computed(
  () =>
    !!(
      rejectQuantity.value ||
      producedQuantity.value ||
      shouldCloseOperation.value
    ),
);

const modalSubtitle = computed(() =>
  [props.op.op_name, props.nextOp?.op_name].filter(Boolean).join(" → "),
);
const modalOnGoingLabel = computed(() => {
  const computedAvailableQuantity = Math.max(
    0,
    availableQuantity.value - rejectQuantity.value || 0,
  );

  return `/ ${computedAvailableQuantity} ${t(
    "scheduling.sidebar__validation_modal.on-going",
  )}`;
});
const titleText = computed(() => {
  return props.nextOp?.op_id
    ? t("scheduling.sidebar__validation_modal.title_send_quantity")
    : t("scheduling.sidebar__validation_modal.title_validate_quantity");
});
const confirmationText = computed(() => {
  const validationTextElements = [];
  const quantityValidationText = props.nextOp?.op_id
    ? t("scheduling.sidebar__validation_modal.send_quantity")
    : t("scheduling.sidebar__validation_modal.validate_quantity");

  if (producedQuantity.value || rejectQuantity.value)
    validationTextElements.push(quantityValidationText);
  if (shouldCloseOperation.value) {
    validationTextElements.push(
      t("scheduling.sidebar__validation_modal.close_operation"),
    );
  }

  const validationText = validationTextElements.join(` ${t("Commons.and")} `);
  const capitalizedValidationText =
    validationText.charAt(0).toUpperCase() + validationText.slice(1);

  return capitalizedValidationText || t("global.valider");
});

function handleProducedQuantityChange(v: string) {
  producedQuantity.value = sanitizeNumeralInput(v);
}
function handleRejectQuantityChange(v: string) {
  rejectQuantity.value = sanitizeNumeralInput(v);
}

const saveOPValidation = () => {
  const isValid = validateForms();
  if (!isValid) return;

  emit("validate-op", {
    addedRejectQuantity: rejectQuantity.value,
    addedProducedQuantity: producedQuantity.value,
    shouldCloseOP: shouldCloseOperation.value,
  });
};
</script>

<template>
  <Modal
    :footer-props="{confirmButtonText: confirmationText}"
    :title="titleText"
    :subtitle="modalSubtitle"
    :is-operation-ongoing="isLoading"
    :can-confirm-modal="hasMadeAnyChange"
    width="600px"
    hide-borders
    hide-scrollbar
    hide-close-icon
    is-dense
    @close-modal="toggleModal"
    @confirm-modal="saveOPValidation"
  >
    <section>
      <div class="op-validation-modal__input-section">
        <span class="text-neutral-secondary text-14">
          {{ t("scheduling.sidebar__validation_modal.quantity_to_send") }}
        </span>

        <div class="op-validation-modal__input-section--inline">
          <FTextField
            :model-value="producedQuantity"
            :placeholder="$t('global.default')"
            :pink-border="validationErrors.producedQuantity"
            type="number"
            outlined
            flex
            @change="(v) => handleProducedQuantityChange(v)"
          />

          <span class="text-neutral-secondary">{{ modalOnGoingLabel }}</span>
        </div>
      </div>

      <div class="op-validation-modal__input-section mt-6">
        <span class="text-neutral-secondary text-14">
          {{ t("scheduling.sidebar__validation_modal.quantity_to_reject") }}
        </span>

        <FTextField
          :model-value="rejectQuantity"
          :placeholder="$t('global.default')"
          :pink-border="validationErrors.rejectQuantity"
          type="number"
          outlined
          @change="(v) => handleRejectQuantityChange(v)"
        />
      </div>

      <div class="op-validation-modal__summary-section mt-6">
        <div
          v-for="(summary, index) in dynamicSummaries"
          :key="index"
          class="op-validation-modal__summary-block bg-neutral-tertiary px-2 py-1"
        >
          <div
            class="op-validation-modal__summary-block-header text-14 text-neutral-tertiary"
          >
            <OpalIcon :name="summary.icon" size="16" />

            <span>
              {{ $t(`scheduling.sidebar__validation_modal.${summary.key}`) }}
            </span>
          </div>

          <span class="text-neutral-secondary display-18 semi-bold">
            {{ summary.value }}
          </span>
        </div>
      </div>

      <div class="border-neutral-primary w-100 mt-6" />

      <FCheck v-model="shouldCloseOperation" class="mt-6" no-padding>
        <template #label>
          <div class="text-14 text-neutral-secondary">
            <span>
              {{ t("scheduling.sidebar__validation_modal.mark_operation_as") }}
            </span>

            <FChip class="ml-2" color="grey-50">
              {{ $t(`operation.status.${OF_STATUS.DONE}`) }}
            </FChip>
          </div>
        </template>
      </FCheck>
    </section>
  </Modal>
</template>

<style scoped lang="scss">
.op-validation-modal__input-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.op-validation-modal__input-section--inline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.op-validation-modal__summary-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.op-validation-modal__summary-block {
  display: flex;
  flex-direction: column;
  height: 56px;
  width: 100%;
  border-radius: 8px;
}

.op-validation-modal__summary-block-header {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgb(var(--v-theme-grey-500));
}

:deep(.v-chip) {
  .chip-content {
    color: rgb(var(--v-theme-grey-500));
  }
}
</style>
